// Upgraded prop kit. Hand-modeled from primitives but composed thoughtfully
// so each kind reads at a glance and the planet has real terrain variety.
// Every mesh is toon-shaded + outlined to match the cel direction.

import * as THREE from 'three';
import { makeToonMaterial, addOutline } from '../render/flat.js';
import { loadManifest, makeProp } from '../assets/loader.js';

// Tiny seedable RNG so the layout is stable round-to-round.
function mulberry32(seed) {
  return function() {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Jitter a primitive's vertices so it stops looking "default".
function roughen(geo, amount = 0.15, seed = 1) {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const j = (Math.sin(i * 12.9 + seed) * 0.5 + 0.5) * amount * 2 - amount;
    pos.setXYZ(i, pos.getX(i) * (1 + j), pos.getY(i) * (1 + j), pos.getZ(i) * (1 + j));
  }
  geo.computeVertexNormals();
  return geo;
}

// ---------- Boulders ----------
// Boulder cluster — 2-4 rocks of different sizes leaning on each other.
export function makeBoulderCluster(seed = 0) {
  const grp = new THREE.Group();
  const rng = mulberry32(seed * 17 + 3);
  const count = 2 + Math.floor(rng() * 3);
  const tones = [0x6e7a90, 0x5a6680, 0x808a9c, 0x7a6f8a];
  for (let i = 0; i < count; i++) {
    const r = 0.6 + rng() * 1.2;
    const geo = roughen(new THREE.IcosahedronGeometry(r, 0), 0.25, seed + i);
    const mesh = new THREE.Mesh(geo, makeToonMaterial({ color: tones[Math.floor(rng() * tones.length)], bands: 3, rim: 0.5 }));
    addOutline(mesh, 0.05);
    mesh.position.set((rng() - 0.5) * 1.2, r * 0.7, (rng() - 0.5) * 1.2);
    mesh.rotation.set(rng() * 0.4, rng() * Math.PI * 2, rng() * 0.4);
    grp.add(mesh);
  }
  grp.userData.kind = 'boulder';
  grp.userData.collideRadius = 1.5;
  return grp;
}

// Mushroom rock — bulbous top on a narrow base. Iconic alien-rock silhouette.
export function makeMushroomRock(seed = 0) {
  const grp = new THREE.Group();
  const rng = mulberry32(seed * 31 + 7);
  const baseColor = [0x5a6580, 0x6a5570, 0x4a5a78][Math.floor(rng() * 3)];
  // Stem
  const stem = new THREE.Mesh(
    roughen(new THREE.CylinderGeometry(0.45, 0.65, 1.6, 8, 2), 0.12, seed),
    makeToonMaterial({ color: baseColor, bands: 3, rim: 0.5 })
  );
  stem.position.y = 0.8;
  addOutline(stem, 0.04);
  grp.add(stem);
  // Cap — flattened sphere
  const cap = new THREE.Mesh(
    roughen(new THREE.SphereGeometry(1.4, 12, 8), 0.18, seed + 1),
    makeToonMaterial({ color: 0x8a7aa0, bands: 3, rim: 0.5 })
  );
  cap.position.y = 1.7;
  cap.scale.y = 0.55;
  addOutline(cap, 0.05);
  grp.add(cap);
  grp.userData.kind = 'mushroom';
  grp.userData.collideRadius = 0.7;
  grp.userData.hideRadius = 1.3; // can hide UNDER the cap
  return grp;
}

// Rock arch — two leaning standing stones with a capstone. Walkable through.
export function makeRockArch(seed = 0) {
  const grp = new THREE.Group();
  const rng = mulberry32(seed * 41 + 11);
  const stoneColor = 0x5e6a82;
  const makeStanding = (x) => {
    const m = new THREE.Mesh(
      roughen(new THREE.BoxGeometry(0.7, 3.0, 0.7), 0.22, seed + x),
      makeToonMaterial({ color: stoneColor, bands: 3, rim: 0.5 })
    );
    addOutline(m, 0.05);
    m.position.set(x, 1.5, 0);
    m.rotation.z = -x * 0.15; // lean inward
    return m;
  };
  grp.add(makeStanding(-1.2));
  grp.add(makeStanding(1.2));
  const cap = new THREE.Mesh(
    roughen(new THREE.BoxGeometry(3.2, 0.5, 0.9), 0.15, seed + 5),
    makeToonMaterial({ color: 0x6e7a92, bands: 3, rim: 0.5 })
  );
  addOutline(cap, 0.05);
  cap.position.y = 3.1;
  grp.add(cap);
  grp.userData.kind = 'arch';
  // Two pillars block, but center is walkable. Approximate as two children with their own collision.
  grp.children[0].userData.collideRadius = 0.5;
  grp.children[1].userData.collideRadius = 0.5;
  grp.userData.collideRadius = 0; // group itself doesn't collide
  return grp;
}

// Cliff outcropping — stack of slab boxes for a layered look. Useful as
// landscape feature you can hide behind.
export function makeCliff(seed = 0) {
  const grp = new THREE.Group();
  const rng = mulberry32(seed * 53 + 13);
  const layers = 3 + Math.floor(rng() * 2);
  let y = 0;
  for (let i = 0; i < layers; i++) {
    const w = 3.0 - i * 0.4 + rng() * 0.4;
    const h = 0.7 + rng() * 0.4;
    const d = 1.8 - i * 0.2;
    const slab = new THREE.Mesh(
      roughen(new THREE.BoxGeometry(w, h, d), 0.12, seed + i),
      makeToonMaterial({ color: 0x4a5570 + i * 0x080808, bands: 3, rim: 0.5 })
    );
    addOutline(slab, 0.04);
    slab.position.set(rng() * 0.3, y + h / 2, rng() * 0.3);
    slab.rotation.y = (rng() - 0.5) * 0.3;
    y += h * 0.85;
    grp.add(slab);
  }
  grp.userData.kind = 'cliff';
  grp.userData.collideRadius = 1.8;
  return grp;
}

// Crystal cluster — glowing shards growing from the rock. Beautiful + diegetic
// light source.
export function makeCrystalCluster(seed = 0) {
  const grp = new THREE.Group();
  const rng = mulberry32(seed * 67 + 17);
  const palettes = [
    { gem: 0xff5dc7, glow: 0xff5dc7 },
    { gem: 0xb84dff, glow: 0xb84dff },
    { gem: 0x6b7dff, glow: 0x9eb0ff },
    { gem: 0x4dffd7, glow: 0x4dffd7 },
  ];
  const palette = palettes[Math.floor(rng() * palettes.length)];
  // Base rock the crystals erupt from
  const base = new THREE.Mesh(
    roughen(new THREE.IcosahedronGeometry(0.6, 0), 0.2, seed),
    makeToonMaterial({ color: 0x3a3450, bands: 3, rim: 0.5 })
  );
  addOutline(base, 0.04);
  base.position.y = 0.3;
  grp.add(base);
  // 3-5 shards
  const count = 3 + Math.floor(rng() * 3);
  for (let i = 0; i < count; i++) {
    const h = 0.7 + rng() * 1.0;
    const shard = new THREE.Mesh(
      new THREE.ConeGeometry(0.13 + rng() * 0.08, h, 4),
      makeToonMaterial({
        color: palette.gem,
        bands: 2, rim: 0.8,
        emissive: palette.glow, emissiveIntensity: 0.7,
      })
    );
    addOutline(shard, 0.025);
    const a = (i / count) * Math.PI * 2 + rng() * 0.4;
    shard.position.set(Math.cos(a) * 0.35, 0.4 + h / 2, Math.sin(a) * 0.35);
    shard.rotation.set((rng() - 0.5) * 0.4, rng() * Math.PI * 2, (rng() - 0.5) * 0.4);
    grp.add(shard);
  }
  // Tiny point light for that cosmic crystal glow
  const light = new THREE.PointLight(palette.glow, 1.6, 8, 2);
  light.position.set(0, 1.2, 0);
  grp.add(light);
  grp.userData.kind = 'crystal';
  grp.userData.collideRadius = 0.6;
  grp.userData.hideRadius = 1.0;
  return grp;
}

// ---------- Vegetation ----------
// Twisted alien tree — bent trunk, branches with leaf-clusters. Hides nicely.
export function makeTwistedTree(seed = 0) {
  const grp = new THREE.Group();
  const rng = mulberry32(seed * 89 + 19);
  const leafColor = [0x4a3a7a, 0x5d4a8a, 0x3a2a6a, 0x6a4f8a][Math.floor(rng() * 4)];
  const trunkMat = makeToonMaterial({ color: 0x3a2818, bands: 3, rim: 0.4 });
  // Trunk: a curved cylinder approximated by 3 segments
  for (let i = 0; i < 3; i++) {
    const seg = new THREE.Mesh(
      new THREE.CylinderGeometry(0.22 - i * 0.04, 0.28 - i * 0.04, 0.7, 7),
      trunkMat
    );
    addOutline(seg, 0.035);
    seg.position.set(Math.sin(i * 0.6) * 0.15, 0.35 + i * 0.6, Math.cos(i * 0.7) * 0.1);
    seg.rotation.z = (rng() - 0.5) * 0.3;
    grp.add(seg);
  }
  // Branches: 2-3 small cylinders sticking out
  for (let i = 0; i < 3; i++) {
    const br = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.08, 0.6, 6),
      trunkMat
    );
    addOutline(br, 0.025);
    const a = (i / 3) * Math.PI * 2;
    br.position.set(Math.cos(a) * 0.3, 1.4 + rng() * 0.5, Math.sin(a) * 0.3);
    br.rotation.z = Math.cos(a) * -1.0;
    br.rotation.x = Math.sin(a) * 1.0;
    grp.add(br);
  }
  // Leaf cluster: 3 overlapping icosahedra
  const leafMat = makeToonMaterial({ color: leafColor, bands: 3, rim: 0.5 });
  for (let i = 0; i < 4; i++) {
    const leaf = new THREE.Mesh(
      roughen(new THREE.IcosahedronGeometry(0.55 + rng() * 0.2, 0), 0.14, seed + i),
      leafMat
    );
    addOutline(leaf, 0.04);
    leaf.position.set((rng() - 0.5) * 0.7, 2.0 + rng() * 0.5, (rng() - 0.5) * 0.7);
    grp.add(leaf);
  }
  grp.userData.kind = 'tree';
  grp.userData.collideRadius = 0.5;
  return grp;
}

// Fallen log — horizontal tree trunk you can crouch behind.
export function makeFallenLog(seed = 0) {
  const grp = new THREE.Group();
  const rng = mulberry32(seed * 97 + 23);
  const log = new THREE.Mesh(
    new THREE.CylinderGeometry(0.4, 0.45, 3.5, 8),
    makeToonMaterial({ color: 0x4a3525, bands: 3, rim: 0.4 })
  );
  log.rotation.z = Math.PI / 2;
  log.position.y = 0.4;
  addOutline(log, 0.04);
  grp.add(log);
  // Mossy patches
  const mossMat = makeToonMaterial({ color: 0x4a3a7a, bands: 3, rim: 0.5 });
  for (let i = 0; i < 3; i++) {
    const moss = new THREE.Mesh(
      roughen(new THREE.IcosahedronGeometry(0.3, 0), 0.15, seed + i),
      mossMat
    );
    addOutline(moss, 0.025);
    moss.position.set(-1.2 + i * 1.2, 0.7, 0);
    moss.scale.set(1, 0.4, 1);
    grp.add(moss);
  }
  grp.userData.kind = 'log';
  grp.userData.collideRadius = 1.6;
  grp.userData.hideRadius = 1.6;
  return grp;
}

// Thicket — dense bush cluster, taller than a bush, blocks sight.
export function makeThicket(seed = 0) {
  const grp = new THREE.Group();
  const rng = mulberry32(seed * 103 + 29);
  const colors = [0x3d8a5a, 0x4ea268, 0x4a3a7a, 0x6a4f8a];
  for (let i = 0; i < 6; i++) {
    const r = 0.5 + rng() * 0.4;
    const ico = new THREE.Mesh(
      roughen(new THREE.IcosahedronGeometry(r, 0), 0.15, seed + i),
      makeToonMaterial({ color: colors[i % colors.length], bands: 3, rim: 0.4 })
    );
    addOutline(ico, 0.04);
    const a = (i / 6) * Math.PI * 2;
    ico.position.set(Math.cos(a) * 0.5 + (rng() - 0.5) * 0.3, 0.4 + rng() * 0.3, Math.sin(a) * 0.5 + (rng() - 0.5) * 0.3);
    grp.add(ico);
  }
  grp.userData.kind = 'thicket';
  grp.userData.collideRadius = 0; // walk-through
  grp.userData.hideRadius = 1.4;
  return grp;
}

// ---------- Single hut (now rare) ----------
export function makeHut() {
  const grp = new THREE.Group();
  const wall = new THREE.Mesh(
    new THREE.BoxGeometry(2.4, 1.8, 2.4),
    makeToonMaterial({ color: 0xe6cb95, bands: 3, rim: 0.5 })
  );
  wall.position.y = 0.9;
  addOutline(wall, 0.05);
  grp.add(wall);
  const roof = new THREE.Mesh(
    new THREE.ConeGeometry(2.0, 1.3, 4),
    makeToonMaterial({ color: 0xc24d3a, bands: 3, rim: 0.5 })
  );
  roof.position.y = 2.4;
  roof.rotation.y = Math.PI / 4;
  addOutline(roof, 0.06);
  grp.add(roof);
  const win = new THREE.Mesh(
    new THREE.PlaneGeometry(0.5, 0.6),
    new THREE.MeshBasicMaterial({ color: 0xffd285 })
  );
  win.position.set(1.21, 1.0, 0);
  win.rotation.y = Math.PI / 2;
  grp.add(win);
  const lamp = new THREE.PointLight(0xffd285, 2.0, 12, 2);
  lamp.position.set(1.5, 1.0, 0);
  grp.add(lamp);
  grp.userData.kind = 'hut';
  grp.userData.collideRadius = 1.6;
  return grp;
}

// ---------- Placement ----------
export function placeOnPlanet(planet, obj, lon, lat) {
  const dir = new THREE.Vector3(
    Math.cos(lat) * Math.cos(lon),
    Math.sin(lat),
    Math.cos(lat) * Math.sin(lon)
  );
  const r = planet.sampleRadius(dir);
  obj.position.copy(dir).multiplyScalar(r);
  obj.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  return obj;
}

// Populate planet with rich variety. One hut. Tons of natural cover.
// Async: awaits the GLTF manifest then routes every prop through `makeProp`,
// which loads a GLTF if registered for the kind or falls back to procedural.
export async function populatePlanet(scene, planet) {
  await loadManifest();

  const rng = mulberry32(7);
  const props = [];

  const add = (obj, lon, lat) => {
    placeOnPlanet(planet, obj, lon, lat);
    scene.add(obj);
    props.push(obj);
    // Children with their own collide radius (e.g. arch pillars) need to be
    // exposed as their own props so the walker can collide.
    obj.children.forEach(c => {
      if (c.userData.collideRadius) {
        // World-space position of the child, recomputed each frame is overkill
        // for static props. Cache once after placement.
        c.updateMatrixWorld(true);
        const wp = new THREE.Vector3();
        c.getWorldPosition(wp);
        props.push({ position: wp, userData: c.userData });
      }
    });
  };

  // ONE hut — kept fully procedural (gameplay element with diegetic board)
  const hut = makeHut();
  add(hut, 0.5, 0.85);

  // Boulder clusters
  for (let i = 0; i < 26; i++) {
    const b = await makeProp('boulder', () => {
      const m = makeBoulderCluster(i);
      m.scale.setScalar(0.7 + rng() * 0.7);
      return m;
    }, { collideRadius: 1.5 });
    add(b, rng() * Math.PI * 2, (rng() - 0.5) * Math.PI * 0.95);
  }

  // Mushroom rocks
  for (let i = 0; i < 15; i++) {
    const m = await makeProp('mushroom', () => {
      const x = makeMushroomRock(i);
      x.scale.setScalar(0.8 + rng() * 0.5);
      return x;
    }, { collideRadius: 0.7, hideRadius: 1.3 });
    add(m, rng() * Math.PI * 2, (rng() - 0.5) * Math.PI * 0.9);
  }

  // Rock arches
  for (let i = 0; i < 7; i++) {
    const a = await makeProp('arch', () => makeRockArch(i), { collideRadius: 0 });
    add(a, rng() * Math.PI * 2, (rng() - 0.5) * Math.PI * 0.85);
  }

  // Cliffs
  for (let i = 0; i < 9; i++) {
    const cl = await makeProp('cliff', () => makeCliff(i), { collideRadius: 1.8 });
    add(cl, rng() * Math.PI * 2, (rng() - 0.5) * Math.PI * 0.9);
  }

  // Crystal clusters
  for (let i = 0; i < 11; i++) {
    const cr = await makeProp('crystal', () => makeCrystalCluster(i), { collideRadius: 0.6, hideRadius: 1.0 });
    add(cr, rng() * Math.PI * 2, (rng() - 0.5) * Math.PI * 0.9);
  }

  // Twisted trees grouped in groves
  for (let g = 0; g < 7; g++) {
    const cLon = rng() * Math.PI * 2;
    const cLat = (rng() - 0.5) * Math.PI * 0.8;
    const count = 4 + Math.floor(rng() * 4);
    for (let i = 0; i < count; i++) {
      const t = await makeProp('tree', () => {
        const tree = makeTwistedTree(g * 100 + i);
        tree.scale.setScalar(0.85 + rng() * 0.5);
        return tree;
      }, { collideRadius: 0.5 });
      add(t, cLon + (rng() - 0.5) * 0.8, cLat + (rng() - 0.5) * 0.5);
    }
  }

  // Fallen logs
  for (let i = 0; i < 9; i++) {
    const l = await makeProp('log', () => makeFallenLog(i), { collideRadius: 1.6, hideRadius: 1.6 });
    add(l, rng() * Math.PI * 2, (rng() - 0.5) * Math.PI * 0.9);
  }

  // Thickets
  for (let i = 0; i < 22; i++) {
    const th = await makeProp('thicket', () => makeThicket(i), { collideRadius: 0, hideRadius: 1.4 });
    add(th, rng() * Math.PI * 2, (rng() - 0.5) * Math.PI * 0.9);
  }

  return { props, huts: [hut] };
}
